'use client';

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import { localeFromPath } from '../../_lib/marketing-routes';
import {
  BANNER_VERZOEGERUNG_MS,
  anderesFensterOffen,
  halbeScrolltiefeErreicht,
  leseBannerZustand,
  pfadAusgeschlossen,
  schreibeBannerZustand,
  seiteTraegtMarker,
  zustandErlaubtBanner,
} from './newsletter-banner-regeln';

/**
 * Insel des Newsletter-Banners im Marketing-Layout (Spec 2026-10-12 §6).
 *
 * Hier liegt nur die Auslöse-Logik (Zeit, Scrolltiefe, Ruhe, Ausschlüsse).
 * Das Banner selbst samt Formular, Server-Aktion und Stil wird erst geladen,
 * wenn es erscheinen soll: nichts davon liegt auf dem LCP-Pfad. Es ist
 * `position: fixed`, verschiebt also kein Layout (CLS 0).
 *
 * Warum im Marketing-Layout: dort ist die Website und nur sie. App, Auth und
 * Onboarding haben eigene Layouts und sehen die Insel nie; die Ausschlüsse
 * innerhalb der Website (`/legal`, `/lp`, `/newsletter`, Marker) prüft
 * `pfadAusgeschlossen`/`seiteTraegtMarker`. Das Layout bleibt über
 * Seitenwechsel bestehen, deshalb wird je Pfad neu gezählt.
 */

const NewsletterBanner = dynamic(
  () => import('./newsletter-banner').then((m) => m.NewsletterBanner),
  { ssr: false },
);

const BREIT = '(min-width: 768px)';
const WARTE_MS = 1_000;

function speicher(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function abonniereBreite(melden: () => void) {
  if (typeof window.matchMedia !== 'function') return () => {};

  const abfrage = window.matchMedia(BREIT);
  abfrage.addEventListener('change', melden);
  return () => abfrage.removeEventListener('change', melden);
}

function istBreit() {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia(BREIT).matches
    : false;
}

type Phase = 'wartet' | 'offen' | 'erledigt';

export function NewsletterBannerInsel() {
  const pathname = usePathname();
  const locale = localeFromPath(pathname);
  const ausgeschlossen = pfadAusgeschlossen(pathname);
  const [phase, setPhase] = useState<Phase>('wartet');
  const breit = useSyncExternalStore(abonniereBreite, istBreit, () => false);

  // Effekt begründet: Zeitgeber und Scroll-Lauscher sind Browser-Abos, die
  // je Seitenaufruf neu gestellt und beim Wechsel abgebaut werden müssen.
  useEffect(() => {
    if (phase !== 'wartet' || ausgeschlossen) return;
    if (!zustandErlaubtBanner(leseBannerZustand(speicher()), new Date())) {
      return;
    }

    let fertig = false;
    let wartet = false;
    let warteZeitgeber: ReturnType<typeof setTimeout> | undefined;

    const abbauen = () => {
      fertig = true;
      clearTimeout(zeitgeber);
      clearTimeout(warteZeitgeber);
      window.removeEventListener('scroll', beimScrollen);
    };

    const zeigen = () => {
      if (fertig) return;
      if (seiteTraegtMarker(document)) {
        abbauen();
        return;
      }
      if (anderesFensterOffen(document)) {
        if (!wartet) {
          wartet = true;
          warteZeitgeber = setTimeout(() => {
            wartet = false;
            zeigen();
          }, WARTE_MS);
        }
        return;
      }
      abbauen();
      setPhase('offen');
    };

    function beimScrollen() {
      if (
        halbeScrolltiefeErreicht({
          scrollY: window.scrollY,
          fensterHoehe: window.innerHeight,
          dokumentHoehe: document.documentElement.scrollHeight,
        })
      ) {
        zeigen();
      }
    }

    const zeitgeber = setTimeout(zeigen, BANNER_VERZOEGERUNG_MS);
    window.addEventListener('scroll', beimScrollen, { passive: true });

    return abbauen;
  }, [phase, ausgeschlossen, pathname]);

  const schliessen = useCallback(() => {
    const bisher = leseBannerZustand(speicher());
    schreibeBannerZustand(speicher(), {
      ...bisher,
      geschlossenAm: new Date().toISOString(),
    });
    setPhase('erledigt');
  }, []);

  const angemeldet = useCallback(() => {
    schreibeBannerZustand(speicher(), { angemeldet: true });
  }, []);

  if (phase !== 'offen' || ausgeschlossen) return null;

  return (
    <NewsletterBanner
      locale={locale}
      art={breit ? 'fenster' : 'leiste'}
      onSchliessen={schliessen}
      onAngemeldet={angemeldet}
    />
  );
}
